'use client';

import { useAuth } from '@/context/auth';
import type { AccountBuyerObjectType, AccountSellerObjectType } from '@/graphql/graphql';
import { getBuyerAccount } from '@/lib/api-buyers';
import { getSellerAccount } from '@/lib/api-sellers';
import { clearDatadogUser, setDatadogUser } from '@/lib/datadog-browser';
import { useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { ANONYMOUS_ID, type UserData, UserDataContext } from './user-data-context';

export function UserDataProvider({ children }: { children: ReactNode }) {
    const { user, loading } = useAuth();

    const [value, setValue] = useState<UserData | null>(null);

    useEffect(() => {
        const groups: string[] = (user?.profile['cognito:groups'] as string[]) ?? [];

        const isAdmin = groups.includes('admin') || groups.includes('admin_ro');
        const isBuyer = groups.includes('buyer');
        const isSeller = groups.includes('seller');
        const isAnonymous = !isAdmin && !isBuyer && !isSeller;
        const isReadOnly = groups.includes('admin_ro');

        const fetchData = async () => {
            try {
                if (isSeller) {
                    const seller = await getSellerAccount(user?.profile['custom:db_id'] as string);
                    setValue({
                        name: seller?.contactName,
                        id: seller?.identifier.id,
                        user: seller,
                        isAdmin,
                        isBuyer,
                        isSeller,
                        isAnonymous,
                        isReadOnly,
                        loading: false,
                    });
                } else if (isBuyer) {
                    const buyer = await getBuyerAccount(user?.profile['custom:db_id'] as string);
                    setValue({
                        name: buyer?.contactName,
                        id: buyer?.identifier.id,
                        user: buyer,
                        isAdmin,
                        isBuyer,
                        isSeller,
                        isAnonymous,
                        isReadOnly,
                        loading: false,
                    });
                } else if (isAdmin) {
                    setValue({
                        name: user?.profile.name as string,
                        id: user?.profile['cognito:username'] as string,
                        user: null,
                        isAdmin,
                        isBuyer,
                        isSeller,
                        isAnonymous,
                        isReadOnly,
                        loading: false,
                    });
                } else {
                    setValue({
                        name: 'anonymous',
                        id: ANONYMOUS_ID,
                        user: null,
                        isAdmin,
                        isBuyer,
                        isSeller,
                        isAnonymous,
                        isReadOnly,
                        loading: false,
                    });
                }
            } catch {
                setValue({
                    name: isAnonymous ? 'anonymous' : ((user?.profile.name as string) ?? ''),
                    id:
                        (isAnonymous
                            ? ANONYMOUS_ID
                            : (user?.profile['custom:db_id'] as string) ||
                              (user?.profile['cognito:username'] as string)) ?? ANONYMOUS_ID,
                    user: null,
                    isAdmin,
                    isBuyer,
                    isSeller,
                    isAnonymous,
                    isReadOnly,
                    loading: false,
                });
            }
        };

        if (!loading) {
            fetchData();
        }
    }, [user, loading]);

    useEffect(() => {
        if (!value || value.loading) return;

        if (value.isAnonymous) {
            void clearDatadogUser().catch(() => undefined);
        } else if (value.id) {
            const email =
                (value.user as AccountSellerObjectType | AccountBuyerObjectType | null)?.contactEmail ??
                (user?.profile.email as string | undefined);

            void setDatadogUser({
                id: value.id,
                name: value.name,
                email,
                role: value.isAdmin ? 'admin' : value.isSeller ? 'seller' : 'buyer',
            }).catch(() => undefined);
        }
    }, [value, user]);

    const memoizedValue = useMemo(() => value, [value]);

    if (!value) return children;

    return <UserDataContext.Provider value={memoizedValue as UserData}>{children}</UserDataContext.Provider>;
}
